/**
 * LocalStore trên IndexedDB. Mọi giá trị được mã hóa AES-GCM bằng khóa thiết bị `CryptoKey`
 * non-extractable (docs/04-crypto-protocol.md §6): đọc được file IndexedDB cũng không có khóa ở dạng thô.
 * Khóa chỉ dùng được bởi chính origin này qua WebCrypto.
 */
import {
  compareMessages,
  type LocalActivity,
  type LocalConversationState,
  type LocalMessage,
  type LocalStore,
  type StoredSession,
} from '@swchat/client-sdk';

const DB_NAME = 'swchat';
const DB_VERSION = 1;
const KV = 'kv';
const MESSAGES = 'messages';
const KEYS = 'keys';

type Sealed = { iv: Uint8Array<ArrayBuffer>; ct: ArrayBuffer };

function req<T>(r: IDBRequest<T>): Promise<T> {
  return new Promise((ok, fail) => {
    r.onsuccess = () => ok(r.result);
    r.onerror = () => fail(r.error);
  });
}

function txDone(tx: IDBTransaction): Promise<void> {
  return new Promise((ok, fail) => {
    tx.oncomplete = () => ok();
    tx.onerror = () => fail(tx.error);
    tx.onabort = () => fail(tx.error);
  });
}

/** JSON hỗ trợ Uint8Array (snapshot MLS). */
const replacer = (_: string, v: unknown) => (v instanceof Uint8Array ? { __u8: Array.from(v) } : v);
const reviver = (_: string, v: unknown) =>
  v && typeof v === 'object' && '__u8' in v ? new Uint8Array((v as { __u8: number[] }).__u8) : v;

export class IdbStore implements LocalStore {
  private constructor(
    private readonly db: IDBDatabase,
    private readonly key: CryptoKey,
  ) {}

  static async open(): Promise<IdbStore> {
    const open = indexedDB.open(DB_NAME, DB_VERSION);
    open.onupgradeneeded = () => {
      const db = open.result;
      db.createObjectStore(KV);
      db.createObjectStore(KEYS);
      const messages = db.createObjectStore(MESSAGES, { keyPath: ['c', 'id'] });
      messages.createIndex('by_conversation', 'c');
    };
    const db = await req(open);
    const tx = db.transaction(KEYS, 'readwrite');
    let key = (await req(tx.objectStore(KEYS).get('device'))) as CryptoKey | undefined;
    if (!key) {
      key = await crypto.subtle.generateKey({ name: 'AES-GCM', length: 256 }, false, ['encrypt', 'decrypt']);
      await req(db.transaction(KEYS, 'readwrite').objectStore(KEYS).put(key, 'device'));
    }
    return new IdbStore(db, key);
  }

  private async seal(value: unknown): Promise<Sealed> {
    const iv = crypto.getRandomValues(new Uint8Array(12));
    const data = new TextEncoder().encode(JSON.stringify(value, replacer));
    return { iv, ct: await crypto.subtle.encrypt({ name: 'AES-GCM', iv }, this.key, data) };
  }

  private async open<T>(sealed: Sealed | undefined): Promise<T | undefined> {
    if (!sealed) return undefined;
    const pt = await crypto.subtle.decrypt({ name: 'AES-GCM', iv: sealed.iv }, this.key, sealed.ct);
    return JSON.parse(new TextDecoder().decode(pt), reviver) as T;
  }

  private async getKv<T>(key: string): Promise<T | undefined> {
    return this.open<T>(await req(this.db.transaction(KV).objectStore(KV).get(key)));
  }

  private async setKv(key: string, value: unknown) {
    const sealed = await this.seal(value);
    const tx = this.db.transaction(KV, 'readwrite');
    tx.objectStore(KV).put(sealed, key);
    await txDone(tx);
  }

  loadSession() {
    return this.getKv<StoredSession>('session');
  }

  saveSession(session: StoredSession) {
    return this.setKv('session', session);
  }

  async clear() {
    const tx = this.db.transaction([KV, MESSAGES], 'readwrite');
    tx.objectStore(KV).clear();
    tx.objectStore(MESSAGES).clear();
    await txDone(tx);
  }

  async getConversationState(id: string): Promise<LocalConversationState> {
    return (await this.getKv<LocalConversationState>(`conv:${id}`)) ?? { cursor: 0, archive_cursor: 0 };
  }

  setConversationState(id: string, state: LocalConversationState) {
    return this.setKv(`conv:${id}`, state);
  }

  async putMessages(messages: LocalMessage[]) {
    const rows = await Promise.all(messages.map(async (m) => ({ c: m.conversation_id, id: m.id, v: await this.seal(m) })));
    const tx = this.db.transaction(MESSAGES, 'readwrite');
    for (const row of rows) tx.objectStore(MESSAGES).put(row);
    await txDone(tx);
  }

  async getMessage(conversationId: string, id: string) {
    const row = (await req(this.db.transaction(MESSAGES).objectStore(MESSAGES).get([conversationId, id]))) as { v: Sealed } | undefined;
    return this.open<LocalMessage>(row?.v);
  }

  async listMessages(conversationId: string, opts?: { limit?: number }) {
    const rows = (await req(
      this.db.transaction(MESSAGES).objectStore(MESSAGES).index('by_conversation').getAll(conversationId),
    )) as { v: Sealed }[];
    const all = (await Promise.all(rows.map((r) => this.open<LocalMessage>(r.v)))).filter((m): m is LocalMessage => !!m);
    all.sort(compareMessages);
    return opts?.limit ? all.slice(-opts.limit) : all;
  }

  getPinnedUsk(userId: string) {
    return this.getKv<string>(`pin:${userId}`);
  }

  setPinnedUsk(userId: string, uskPub: string) {
    return this.setKv(`pin:${userId}`, uskPub);
  }

  async getActivity(conversationId: string): Promise<LocalActivity[]> {
    return (await this.getKv<LocalActivity[]>(`activity:${conversationId}`)) ?? [];
  }

  setActivity(conversationId: string, items: LocalActivity[]) {
    return this.setKv(`activity:${conversationId}`, items);
  }

  async deleteConversationData(conversationId: string) {
    const tx = this.db.transaction([KV, MESSAGES], 'readwrite');
    tx.objectStore(KV).delete(`activity:${conversationId}`);
    const store = tx.objectStore(MESSAGES);
    const cursorReq = store.index('by_conversation').openKeyCursor(IDBKeyRange.only(conversationId));
    cursorReq.onsuccess = () => {
      const cursor = cursorReq.result;
      if (!cursor) return;
      store.delete(cursor.primaryKey);
      cursor.continue();
    };
    await txDone(tx);
  }
}
